import { CompetingBiddersService } from '@http/clients/reporting-app/competing-bidders.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatCurrencyCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    competingBidderEditorConfig,
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const CompetingBidders: IReportConfiguration<CompetingBiddersService> = {
    base: {
        type: ReportType.CompetingBidders,
        serviceType: CompetingBiddersService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'accepted', label: 'Accepted' },
                        { name: 'rejected', label: 'Rejected' }
                    ],
                    rows: [
                        { name: 'ProjectQty', label: 'Qty' },
                        { name: 'Percentage', label: 'Percentage', format: 'percent' }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Competing Bidders by Accepted Total Sell Price'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'bidder',
                            yKey: 'acceptedSellPrice',
                            yName: 'Accepted Total Sell Price'
                        },
                        {
                            type: 'line',
                            xKey: 'bidder',
                            yKey: 'rejectedSellPrice',
                            yName: 'Rejected Total Sell Price'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['acceptedSellPrice', 'rejectedSellPrice'],

                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'acceptedTotalSellPrice',
                            yName: 'Accepted Total Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'rejectedTotalSellPrice',
                            yName: 'Rejected Total Sell Price'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['acceptedTotalSellPrice', 'rejectedTotalSellPrice'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    { field: 'bidder', headerName: 'Competing Bidder ', enableRowGroup: true, lockVisible: true },
                    {
                        field: 'acceptedSellPrice',
                        headerName: 'Accepted Total Sell Price',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'acceptedGrossMarginPercentage',
                        headerName: 'Accepted Total GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'acceptedProjectQty', headerName: 'Accepted Projects Qty', aggFunc: 'sum' },
                    { field: 'acceptedPercentage', headerName: 'Accepted (%)', type: 'percentage', aggFunc: 'avg' },
                    {
                        field: 'rejectedSellPrice',
                        headerName: 'Rejected Total Sell Price',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'rejectedGrossMarginPercentage',
                        headerName: 'Rejected Total GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'rejectedProjectQty', headerName: 'Rejected Projects Qty', aggFunc: 'sum' },
                    { field: 'rejectedPercentage', headerName: 'Rejected (%)', type: 'percentage', aggFunc: 'avg' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            competingBidderEditorConfig
        ]
    }
};
